import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';

interface DashboardStatistics {
  total_connectors: number;
  active_connectors: number;
  total_analyzers: number;
  active_analyzers: number;
  messages_today: number;
  test_results_today: number;
  unacknowledged_alerts: number;
  critical_alerts: number;
  average_quality_score: number; // maps to qc style metric
  system_uptime: string; // percentage string e.g. "99.9%"
}

interface DashboardAlert {
  id: string;
  device_name: string;
  alert_type: string;
  category: string;
  severity: string;
  title?: string;
  message: string;
  time_ago?: string;
  created_at: string;
  is_acknowledged: boolean;
}

interface DashboardApiResponse {
  statistics: DashboardStatistics;
  recent_connectors: any[];
  recent_test_results: any[];
  recent_alerts: DashboardAlert[];
  generated_at: string;
}

export function useLabLinkDashboard(refreshIntervalMs: number = 60000) {
  const [data, setData] = useState<DashboardApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const endpoint = '/v1/lablink/api/dashboard/';

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await apiClient.get<DashboardApiResponse>(endpoint);
    if (result.error) {
      setError(result.error);
    } else {
      setData(result.data);
    }
    setLoading(false);
  }, [endpoint]);

  useEffect(() => {
    fetchData();
    if (refreshIntervalMs > 0) {
      const id = setInterval(fetchData, refreshIntervalMs);
      return () => clearInterval(id);
    }
  }, [fetchData, refreshIntervalMs]);

  return { data, loading, error, refetch: fetchData };
}

export type { DashboardStatistics, DashboardAlert, DashboardApiResponse };
